// src/components/DemocraticFrameworkTab.jsx
import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, AlertTriangle, Shield, BarChart2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import Modal from './Modal';
import SelectPillarForm from './SelectPillarForm';
import SelectTacticForm from './SelectTacticForm';
import SelectPrecedentForm from './SelectPrecedentForm';
import DemocracyIndicesTab from './DemocracyIndicesTab';
import ManageQuoteActions from './ManageQuoteActions';

const DemocraticFrameworkTab = () => {
  // State for data
  const [pillars, setPillars] = useState([]);
  const [tactics, setTactics] = useState([]);
  const [precedents, setPrecedents] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // UI state
  const [activeSection, setActiveSection] = useState('connections');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Modal state
  const [showPillarModal, setShowPillarModal] = useState(false);
  const [showTacticModal, setShowTacticModal] = useState(false);
  const [showPrecedentModal, setShowPrecedentModal] = useState(false);
  
  // Edit modals state
  const [showEditPillarModal, setShowEditPillarModal] = useState(false);
  const [showEditTacticModal, setShowEditTacticModal] = useState(false);
  const [showEditPrecedentModal, setShowEditPrecedentModal] = useState(false);
  const [editingPillarConnection, setEditingPillarConnection] = useState(null);
  const [editingTacticConnection, setEditingTacticConnection] = useState(null);
  const [editingPrecedentConnection, setEditingPrecedentConnection] = useState(null);
  const [editJustificationText, setEditJustificationText] = useState('');
  const [editImpactLevel, setEditImpactLevel] = useState('');
  
  // Editing state
  const [editingPillar, setEditingPillar] = useState(null);
  const [editingTactic, setEditingTactic] = useState(null);
  const [editingPrecedent, setEditingPrecedent] = useState(null);
  
  // Form data
  const [pillarForm, setPillarForm] = useState({
    name: '',
    description: '',
    source_definition_url: ''
  });
  
  const [tacticForm, setTacticForm] = useState({
    name: '',
    description: '',
    playbook_step_number: '',
    source_url: ''
  });
  
  const [precedentForm, setPrecedentForm] = useState({
    country: '',
    time_period: '',
    description: '',
    outcome: '',
    source_urls: ''
  });
  
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [quoteConnections, setQuoteConnections] = useState({
    pillars: [],
    tactics: [],
    precedents: []
  });
  
  // Load data when component mounts
  useEffect(() => {
    loadData();
  }, []);
  
  // Load framework data
  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Load pillars
      const pillarsResponse = await fetch('/api/admin/pillars');
      if (!pillarsResponse.ok) {
        throw new Error('Failed to fetch democratic pillars');
      }
      const pillarsData = await pillarsResponse.json();
      setPillars(pillarsData);
      
      // Load tactics
      const tacticsResponse = await fetch('/api/admin/tactics');
      if (!tacticsResponse.ok) {
        throw new Error('Failed to fetch authoritarian tactics');
      }
      const tacticsData = await tacticsResponse.json();
      setTactics(tacticsData);
      
      // Load precedents
      const precedentsResponse = await fetch('/api/admin/precedents');
      if (!precedentsResponse.ok) {
        throw new Error('Failed to fetch historical precedents');
      }
      const precedentsData = await precedentsResponse.json();
      setPrecedents(precedentsData);
      
      // Load quotes (basic info) with pagination parameters to get all quotes
      const quotesResponse = await fetch('/api/admin/quotes?pageSize=1000');
      if (!quotesResponse.ok) {
        throw new Error('Failed to fetch quotes');
      }
      const quotesData = await quotesResponse.json();
      // Handle either array directly or object with quotes property
      const quotesArray = Array.isArray(quotesData) ? quotesData : (quotesData.quotes || []);
      console.log('Loaded quotes:', quotesArray.length);
      
      // If we received quotes in paginated format, check if we need to load more
      if (!Array.isArray(quotesData) && quotesData.pagination && quotesData.pagination.totalQuotes > quotesArray.length) {
        console.log(`Loading all ${quotesData.pagination.totalQuotes} quotes...`);
        
        // Load all quotes - this might be a large request
        const allQuotesResponse = await fetch(`/api/admin/quotes?pageSize=${quotesData.pagination.totalQuotes}`);
        if (allQuotesResponse.ok) {
          const allQuotesData = await allQuotesResponse.json();
          const allQuotesArray = allQuotesData.quotes || [];
          console.log(`Successfully loaded all ${allQuotesArray.length} quotes`);
          setQuotes(allQuotesArray);
          setFilteredQuotes(allQuotesArray);
        } else {
          // If loading all quotes fails, we'll use what we have
          console.warn('Could not load all quotes, using partial set');
          setQuotes(quotesArray);
          setFilteredQuotes(quotesArray);
        }
      } else {
        setQuotes(quotesArray);
        setFilteredQuotes(quotesArray);
      }
    } catch (error) {
      console.error('Error loading framework data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Load connections for a specific quote
  const loadQuoteConnections = async (quoteId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/admin/quotes/${quoteId}/framework`);
      if (!response.ok) {
        throw new Error('Failed to fetch quote framework data');
      }
      
      const data = await response.json();
      setQuoteConnections({
        pillars: data.pillars || [],
        tactics: data.tactics || [],
        precedents: data.precedents || []
      });
      
      // Also set the selected quote
      setSelectedQuote(data.quote);
    } catch (error) {
      console.error('Error loading quote connections:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle form submissions
  const handlePillarSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      // Validation
      if (!pillarForm.name || !pillarForm.description) {
        throw new Error('Name and description are required');
      }
      
      // Create or update pillar
      const method = editingPillar ? 'PUT' : 'POST';
      const url = editingPillar 
        ? `/api/admin/pillars/${editingPillar.id}` 
        : '/api/admin/pillars';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pillarForm)
      });
      
      if (!response.ok) {
        throw new Error('Failed to save democratic pillar');
      }
      
      // Reset form and reload data
      setPillarForm({
        name: '',
        description: '',
        source_definition_url: ''
      });
      setEditingPillar(null);
      await loadData();
      
      alert(editingPillar ? 'Pillar updated successfully' : 'Pillar added successfully');
    } catch (error) {
      console.error('Error saving pillar:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleTacticSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      // Validation
      if (!tacticForm.name || !tacticForm.description || !tacticForm.playbook_step_number) {
        throw new Error('Name, description, and step number are required');
      }
      
      // Create or update tactic
      const method = editingTactic ? 'PUT' : 'POST';
      const url = editingTactic 
        ? `/api/admin/tactics/${editingTactic.id}` 
        : '/api/admin/tactics';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tacticForm)
      });
      
      if (!response.ok) {
        throw new Error('Failed to save authoritarian tactic');
      }
      
      // Reset form and reload data
      setTacticForm({
        name: '',
        description: '',
        playbook_step_number: '',
        source_url: ''
      });
      setEditingTactic(null);
      await loadData();
      
      alert(editingTactic ? 'Tactic updated successfully' : 'Tactic added successfully');
    } catch (error) {
      console.error('Error saving tactic:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handlePrecedentSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      // Validation
      if (!precedentForm.country || !precedentForm.time_period || !precedentForm.description || !precedentForm.outcome) {
        throw new Error('Country, time period, description, and outcome are required');
      }
      
      // Format source URLs as JSON array
      let formattedUrls = [];
      if (precedentForm.source_urls) {
        // Split by newlines and filter empty lines
        formattedUrls = precedentForm.source_urls
          .split('\n')
          .map(url => url.trim())
          .filter(url => url);
      }
      
      const dataToSend = {
        ...precedentForm,
        source_urls: JSON.stringify(formattedUrls)
      };
      
      // Create or update precedent
      const method = editingPrecedent ? 'PUT' : 'POST';
      const url = editingPrecedent 
        ? `/api/admin/precedents/${editingPrecedent.id}` 
        : '/api/admin/precedents';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSend)
      });
      
      if (!response.ok) {
        throw new Error('Failed to save historical precedent');
      }
      
      // Reset form and reload data
      setPrecedentForm({
        country: '',
        time_period: '',
        description: '',
        outcome: '',
        source_urls: ''
      });
      setEditingPrecedent(null);
      await loadData();
      
      alert(editingPrecedent ? 'Precedent updated successfully' : 'Precedent added successfully');
    } catch (error) {
      console.error('Error saving precedent:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle connecting a quote to a pillar
  const handleConnectQuoteToPillar = async (quoteId, pillarId, impactLevel, justification) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/admin/quotes/${quoteId}/pillars`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pillar_id: pillarId,
          impact_level: impactLevel,
          justification
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to connect quote to pillar');
      }
      
      // Reload connections
      await loadQuoteConnections(quoteId);
      
      alert('Quote connected to pillar successfully');
    } catch (error) {
      console.error('Error connecting quote to pillar:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle editing a pillar connection justification
  const handleEditPillarJustification = async (quoteId, pillarId, justification, impactLevel) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/admin/quotes/${quoteId}/pillars/${pillarId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          justification,
          impact_level: impactLevel
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update pillar justification');
      }
      
      // Reload connections
      await loadQuoteConnections(quoteId);
      
      alert('Detailed threat analysis updated successfully');
    } catch (error) {
      console.error('Error updating pillar justification:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle connecting a quote to a tactic
  const handleConnectQuoteToTactic = async (quoteId, tacticId, justification) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/admin/quotes/${quoteId}/tactics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tactic_id: tacticId,
          justification
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to connect quote to tactic');
      }
      
      // Reload connections
      await loadQuoteConnections(quoteId);
      
      alert('Quote connected to tactic successfully');
    } catch (error) {
      console.error('Error connecting quote to tactic:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle editing a tactic connection justification
  const handleEditTacticJustification = async (quoteId, tacticId, justification) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/admin/quotes/${quoteId}/tactics/${tacticId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          justification
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update tactic justification');
      }
      
      // Reload connections
      await loadQuoteConnections(quoteId);
      
      alert('Authoritarian tactic justification updated successfully');
    } catch (error) {
      console.error('Error updating tactic justification:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle connecting a quote to a precedent
  const handleConnectQuoteToPrecedent = async (quoteId, precedentId, relevanceNote) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/admin/quotes/${quoteId}/precedents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          precedent_id: precedentId,
          relevance_note: relevanceNote
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to connect quote to precedent');
      }
      
      // Reload connections
      await loadQuoteConnections(quoteId);
      
      alert('Quote connected to precedent successfully');
    } catch (error) {
      console.error('Error connecting quote to precedent:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle quote search filtering
  const handleSearchTermChange = (term) => {
    setSearchTerm(term);
    
    if (!term.trim()) {
      // If search term is empty, show all quotes
      setFilteredQuotes(quotes);
      return;
    }
    
    // Check if term is a potential ID (only numbers)
    const isNumeric = /^\d+$/.test(term);
    
    // If searching for an ID, prioritize exact ID match first
    if (isNumeric) {
      const exactIdMatch = quotes.find(quote => quote.id === parseInt(term, 10));
      if (exactIdMatch) {
        setFilteredQuotes([exactIdMatch]);
        return;
      }
    }
    
    // Filter quotes based on search term
    const lowercaseTerm = term.toLowerCase();
    const filtered = quotes.filter(quote => 
      (quote.claim_text && quote.claim_text.toLowerCase().includes(lowercaseTerm)) ||
      (quote.claim_source && quote.claim_source.toLowerCase().includes(lowercaseTerm)) ||
      (quote.text && quote.text.toLowerCase().includes(lowercaseTerm)) ||
      (quote.source && quote.source.toLowerCase().includes(lowercaseTerm)) ||
      (quote.id && quote.id.toString().includes(term))
    );
    
    setFilteredQuotes(filtered);
  };
  
  // Handle editing a precedent connection relevance note
  const handleEditPrecedentRelevance = async (quoteId, precedentId, relevanceNote) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/admin/quotes/${quoteId}/precedents/${precedentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          relevance_note: relevanceNote
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update precedent relevance');
      }
      
      // Reload connections
      await loadQuoteConnections(quoteId);
      
      alert('Historical precedent relevance updated successfully');
    } catch (error) {
      console.error('Error updating precedent relevance:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Edit form handlers
  const handleEditPillar = (pillar) => {
    setEditingPillar(pillar);
    setPillarForm({
      name: pillar.name,
      description: pillar.description,
      source_definition_url: pillar.source_definition_url || ''
    });
  };
  
  const handleEditTactic = (tactic) => {
    setEditingTactic(tactic);
    setTacticForm({
      name: tactic.name,
      description: tactic.description,
      playbook_step_number: tactic.playbook_step_number,
      source_url: tactic.source_url || ''
    });
  };
  
  const handleEditPrecedent = (precedent) => {
    setEditingPrecedent(precedent);
    // Format source URLs from JSON array to newline-separated string
    const sourceUrlsString = Array.isArray(precedent.source_urls) 
      ? precedent.source_urls.join('\n')
      : typeof precedent.source_urls === 'string'
        ? JSON.parse(precedent.source_urls).join('\n')
        : '';
        
    setPrecedentForm({
      country: precedent.country,
      time_period: precedent.time_period,
      description: precedent.description,
      outcome: precedent.outcome,
      source_urls: sourceUrlsString
    });
  };
  
  // Cancel edit handlers
  const cancelEditPillar = () => {
    setEditingPillar(null);
    setPillarForm({
      name: '',
      description: '',
      source_definition_url: ''
    });
  };
  
  const cancelEditTactic = () => {
    setEditingTactic(null);
    setTacticForm({
      name: '',
      description: '',
      playbook_step_number: '',
      source_url: ''
    });
  };
  
  const cancelEditPrecedent = () => {
    setEditingPrecedent(null);
    setPrecedentForm({
      country: '',
      time_period: '',
      description: '',
      outcome: '',
      source_urls: ''
    });
  };
  
  // Handle deleting a pillar
  const deletePillar = async (pillarId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/admin/pillars/${pillarId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || 'Failed to delete pillar');
      }
      
      // Reload data after successful deletion
      await loadData();
      
      alert('Pillar deleted successfully');
    } catch (error) {
      console.error('Error deleting pillar:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading && !pillars.length && !tactics.length) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-gray-600">Loading framework data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-red-500" />
            </div>
            <div className="ml-3">
              <p className="text-sm">Error: {error}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Section Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex">
          <button
            className={`${
              activeSection === 'pillars'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } py-4 px-6 font-medium text-sm border-b-2 transition duration-150 ease-in-out focus:outline-none`}
            onClick={() => setActiveSection('pillars')}
          >
            Democratic Pillars
          </button>
          <button
            className={`${
              activeSection === 'tactics'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } py-4 px-6 font-medium text-sm border-b-2 transition duration-150 ease-in-out focus:outline-none`}
            onClick={() => setActiveSection('tactics')}
          >
            Authoritarian Tactics
          </button>
          <button
            className={`${
              activeSection === 'precedents'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } py-4 px-6 font-medium text-sm border-b-2 transition duration-150 ease-in-out focus:outline-none`}
            onClick={() => setActiveSection('precedents')}
          >
            Historical Precedents
          </button>
          <button
            className={`${
              activeSection === 'connections'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } py-4 px-6 font-medium text-sm border-b-2 transition duration-150 ease-in-out focus:outline-none`}
            onClick={() => setActiveSection('connections')}
          >
            Quote Connections
          </button>
          <button
            className={`${
              activeSection === 'indices'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } py-4 px-6 font-medium text-sm border-b-2 transition duration-150 ease-in-out focus:outline-none`}
            onClick={() => setActiveSection('indices')}
          >
            <BarChart2 size={16} className="inline-block mr-1" />
            Democracy Indices
          </button>
        </nav>
      </div>
      
      {/* Democratic Pillars Section */}
      {activeSection === 'pillars' && (
        <div>
          <div className="mb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-2">
              {editingPillar ? 'Edit Democratic Pillar' : 'Add New Democratic Pillar'}
            </h2>
            <form onSubmit={handlePillarSubmit} className="bg-gray-50 p-4 rounded-md border">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label htmlFor="pillarName" className="block text-sm font-medium text-gray-700 mb-1">
                    Pillar Name
                  </label>
                  <input
                    type="text"
                    id="pillarName"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={pillarForm.name}
                    onChange={(e) => setPillarForm({ ...pillarForm, name: e.target.value })}
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="pillarDescription" className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    id="pillarDescription"
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={pillarForm.description}
                    onChange={(e) => setPillarForm({ ...pillarForm, description: e.target.value })}
                    required
                  ></textarea>
                </div>
                
                <div>
                  <label htmlFor="pillarSourceUrl" className="block text-sm font-medium text-gray-700 mb-1">
                    Source Definition URL
                  </label>
                  <input
                    type="url"
                    id="pillarSourceUrl"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={pillarForm.source_definition_url}
                    onChange={(e) => setPillarForm({ ...pillarForm, source_definition_url: e.target.value })}
                    placeholder="https://example.com/definition"
                  />
                </div>
                
                <div className="flex justify-end space-x-2">
                  {editingPillar && (
                    <button
                      type="button"
                      onClick={cancelEditPillar}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Cancel
                    </button>
                  )}
                  <button
                    type="submit"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <Save className="h-4 w-4 mr-1" />
                    {editingPillar ? 'Update Pillar' : 'Add Pillar'}
                  </button>
                </div>
              </div>
            </form>
          </div>
          
          <h3 className="text-lg font-medium text-gray-900 mb-2">Democratic Pillars</h3>
          <div className="bg-white shadow overflow-hidden rounded-md">
            <ul className="divide-y divide-gray-200">
              {pillars.length === 0 ? (
                <li className="px-6 py-4 text-gray-500">No democratic pillars defined yet.</li>
              ) : (
                pillars.map((pillar) => (
                  <li key={pillar.id} className="px-6 py-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="text-md font-semibold text-gray-800">{pillar.name}</h4>
                        <p className="text-sm text-gray-600 mt-1">{pillar.description}</p>
                        {pillar.source_definition_url && (
                          <a 
                            href={pillar.source_definition_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline mt-2 inline-block"
                          >
                            Source Definition
                          </a>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditPillar(pillar)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => {
                            if (confirm('Are you sure you want to delete this pillar?')) {
                              deletePillar(pillar.id);
                            }
                          }}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      )}
      
      {/* Authoritarian Tactics Section */}
      {activeSection === 'tactics' && (
        <div>
          <div className="mb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-2">
              {editingTactic ? 'Edit Authoritarian Tactic' : 'Add New Authoritarian Tactic'}
            </h2>
            <form onSubmit={handleTacticSubmit} className="bg-gray-50 p-4 rounded-md border">
              <div className="grid grid-cols-1 gap-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="md:col-span-3">
                    <label htmlFor="tacticName" className="block text-sm font-medium text-gray-700 mb-1">
                      Tactic Name
                    </label>
                    <input
                      type="text"
                      id="tacticName"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      value={tacticForm.name}
                      onChange={(e) => setTacticForm({ ...tacticForm, name: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="tacticStep" className="block text-sm font-medium text-gray-700 mb-1">
                      Playbook Step #
                    </label>
                    <input
                      type="number"
                      id="tacticStep"
                      min="1"
                      max="10"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      value={tacticForm.playbook_step_number}
                      onChange={(e) => setTacticForm({ ...tacticForm, playbook_step_number: e.target.value })}
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="tacticDescription" className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    id="tacticDescription"
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={tacticForm.description}
                    onChange={(e) => setTacticForm({ ...tacticForm, description: e.target.value })}
                    required
                  ></textarea>
                </div>
                
                <div>
                  <label htmlFor="tacticSourceUrl" className="block text-sm font-medium text-gray-700 mb-1">
                    Source URL
                  </label>
                  <input
                    type="url"
                    id="tacticSourceUrl"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={tacticForm.source_url}
                    onChange={(e) => setTacticForm({ ...tacticForm, source_url: e.target.value })}
                    placeholder="https://example.com/authoritarian-playbook"
                  />
                </div>
                
                <div className="flex justify-end space-x-2">
                  {editingTactic && (
                    <button
                      type="button"
                      onClick={cancelEditTactic}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Cancel
                    </button>
                  )}
                  <button
                    type="submit"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <Save className="h-4 w-4 mr-1" />
                    {editingTactic ? 'Update Tactic' : 'Add Tactic'}
                  </button>
                </div>
              </div>
            </form>
          </div>
          
          <h3 className="text-lg font-medium text-gray-900 mb-2">Authoritarian Tactics</h3>
          <div className="bg-white shadow overflow-hidden rounded-md">
            <ul className="divide-y divide-gray-200">
              {tactics.length === 0 ? (
                <li className="px-6 py-4 text-gray-500">No authoritarian tactics defined yet.</li>
              ) : (
                tactics
                  .sort((a, b) => a.playbook_step_number - b.playbook_step_number)
                  .map((tactic) => (
                    <li key={tactic.id} className="px-6 py-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center">
                            <div className="bg-purple-100 text-purple-800 text-xs font-semibold px-2 py-1 rounded-full mr-2">
                              Step {tactic.playbook_step_number}
                            </div>
                            <h4 className="text-md font-semibold text-gray-800">{tactic.name}</h4>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{tactic.description}</p>
                          {tactic.source_url && (
                            <a 
                              href={tactic.source_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline mt-2 inline-block"
                            >
                              Source
                            </a>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditTactic(tactic)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Edit className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => {
                              if (confirm('Are you sure you want to delete this tactic?')) {
                                fetch(`/api/admin/tactics/${tactic.id}`, {
                                  method: 'DELETE'
                                })
                                .then(response => {
                                  if (!response.ok) {
                                    throw new Error('Failed to delete tactic');
                                  }
                                  return response.json();
                                })
                                .then(() => {
                                  loadData(); // Reload data after deletion
                                  alert('Tactic deleted successfully');
                                })
                                .catch(error => {
                                  console.error('Error deleting tactic:', error);
                                  setError(error.message);
                                });
                              }
                            }}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))
              )}
            </ul>
          </div>
        </div>
      )}
      
      {/* Historical Precedents Section */}
      {activeSection === 'precedents' && (
        <div>
          <div className="mb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-2">
              {editingPrecedent ? 'Edit Historical Precedent' : 'Add New Historical Precedent'}
            </h2>
            <form onSubmit={handlePrecedentSubmit} className="bg-gray-50 p-4 rounded-md border">
              <div className="grid grid-cols-1 gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="precedentCountry" className="block text-sm font-medium text-gray-700 mb-1">
                      Country
                    </label>
                    <input
                      type="text"
                      id="precedentCountry"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      value={precedentForm.country}
                      onChange={(e) => setPrecedentForm({ ...precedentForm, country: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="precedentTimePeriod" className="block text-sm font-medium text-gray-700 mb-1">
                      Time Period
                    </label>
                    <input
                      type="text"
                      id="precedentTimePeriod"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      value={precedentForm.time_period}
                      onChange={(e) => setPrecedentForm({ ...precedentForm, time_period: e.target.value })}
                      placeholder="e.g., 2010-2020"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="precedentDescription" className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    id="precedentDescription"
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={precedentForm.description}
                    onChange={(e) => setPrecedentForm({ ...precedentForm, description: e.target.value })}
                    required
                  ></textarea>
                </div>
                
                <div>
                  <label htmlFor="precedentOutcome" className="block text-sm font-medium text-gray-700 mb-1">
                    Outcome
                  </label>
                  <textarea
                    id="precedentOutcome"
                    rows="2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={precedentForm.outcome}
                    onChange={(e) => setPrecedentForm({ ...precedentForm, outcome: e.target.value })}
                    required
                  ></textarea>
                </div>
                
                <div>
                  <label htmlFor="precedentSourceUrls" className="block text-sm font-medium text-gray-700 mb-1">
                    Source URLs (one per line)
                  </label>
                  <textarea
                    id="precedentSourceUrls"
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={precedentForm.source_urls}
                    onChange={(e) => setPrecedentForm({ ...precedentForm, source_urls: e.target.value })}
                    placeholder="https://example.com/source1&#10;https://example.com/source2"
                  ></textarea>
                </div>
                
                <div className="flex justify-end space-x-2">
                  {editingPrecedent && (
                    <button
                      type="button"
                      onClick={cancelEditPrecedent}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Cancel
                    </button>
                  )}
                  <button
                    type="submit"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <Save className="h-4 w-4 mr-1" />
                    {editingPrecedent ? 'Update Precedent' : 'Add Precedent'}
                  </button>
                </div>
              </div>
            </form>
          </div>
          
          <h3 className="text-lg font-medium text-gray-900 mb-2">Historical Precedents</h3>
          <div className="bg-white shadow overflow-hidden rounded-md">
            <ul className="divide-y divide-gray-200">
              {precedents.length === 0 ? (
                <li className="px-6 py-4 text-gray-500">No historical precedents defined yet.</li>
              ) : (
                precedents.map((precedent) => (
                  <li key={precedent.id} className="px-6 py-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="text-md font-semibold text-gray-800">
                          {precedent.country} <span className="text-gray-500 font-normal">({precedent.time_period})</span>
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">{precedent.description}</p>
                        <p className="text-sm text-gray-600 mt-1">
                          <span className="font-medium">Outcome:</span> {precedent.outcome}
                        </p>
                        {precedent.source_urls && (
                          <div className="mt-2">
                            <span className="text-xs text-gray-500">Sources:</span>
                            <div className="mt-1 text-xs">
                              {Array.isArray(precedent.source_urls) 
                                ? precedent.source_urls.map((url, i) => (
                                    <a 
                                      key={i}
                                      href={url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:underline block"
                                    >
                                      {url}
                                    </a>
                                  ))
                                : (typeof precedent.source_urls === 'string' 
                                    ? JSON.parse(precedent.source_urls).map((url, i) => (
                                        <a 
                                          key={i}
                                          href={url} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="text-blue-600 hover:underline block"
                                        >
                                          {url}
                                        </a>
                                      ))
                                    : null
                                  )
                              }
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditPrecedent(precedent)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => {
                            if (confirm('Are you sure you want to delete this precedent?')) {
                              // Implement delete functionality
                              alert('Delete functionality will be implemented');
                            }
                          }}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      )}
      
      {/* Quote Connections Section */}
      {activeSection === 'connections' && (
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-4">Connect Claims to Democratic Framework</h2>
          
          <div className="mb-6 bg-gray-50 p-4 rounded-md border">
            <h3 className="text-md font-medium text-gray-800 mb-2">Find a claim to edit:</h3>
            
            <div className="mb-3">
              <label htmlFor="quoteSearch" className="block text-sm font-medium text-gray-700 mb-1">
                Search by ID, text, or source:
              </label>
              <input
                type="text"
                id="quoteSearch"
                placeholder="Type to search claims..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => handleSearchTermChange(e.target.value)}
              />
              <div className="mt-1 text-sm text-gray-500">
                {filteredQuotes.length} claims found
              </div>
            </div>
            
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              value={selectedQuote ? selectedQuote.id : ''}
              onChange={(e) => {
                const quoteId = e.target.value;
                if (quoteId) {
                  loadQuoteConnections(quoteId);
                } else {
                  setSelectedQuote(null);
                  setQuoteConnections({ pillars: [], tactics: [], precedents: [] });
                }
              }}
              size={Math.min(10, filteredQuotes.length > 0 ? filteredQuotes.length + 1 : 2)}
            >
              <option value="">Select a claim</option>
              {Array.isArray(filteredQuotes) && filteredQuotes.map(quote => (
                <option key={quote.id} value={quote.id}>
                  ID: {quote.id} - {quote.claim_text ? (quote.claim_text.substring(0, 60) + '...') : (quote.text && quote.text.substring(0, 60) + '...')}
                </option>
              ))}
            </select>
            {filteredQuotes.length === 0 && searchTerm && (
              <div className="mt-2 text-sm text-red-500">
                No claims match your search. Try different keywords.
              </div>
            )}
          </div>
          
          {selectedQuote && (
            <div className="space-y-6">
              <div className="bg-white shadow overflow-hidden rounded-md">
                <div className="px-6 py-4 border-b">
                  <h3 className="text-lg font-medium text-gray-900">Selected Claim</h3>
                </div>
                <div className="px-6 py-4">
                  <div className="text-sm text-gray-500 mb-2">ID: {selectedQuote.id}</div>
                  <div className="font-medium text-gray-800 mb-2">
                    {selectedQuote.claim_text || selectedQuote.text}
                  </div>
                  <div className="text-sm text-gray-600">
                    Source: {selectedQuote.claim_source || selectedQuote.source}
                  </div>
                  <div className="text-sm text-gray-600">
                    Date: {selectedQuote.claim_date || selectedQuote.date}
                  </div>
                  <div className="mt-4">
                    <ManageQuoteActions quoteId={selectedQuote.id} />
                  </div>
                </div>
              </div>
              
              {/* Connect to Democratic Pillars */}
              <div className="bg-white shadow overflow-hidden rounded-md">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">Democratic Pillars</h3>
                  <button 
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    onClick={() => setShowPillarModal(true)}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Connect to Pillar
                  </button>
                </div>
                <ul className="divide-y divide-gray-200">
                  {quoteConnections.pillars.length === 0 ? (
                    <li className="px-6 py-4 text-gray-500">No connected pillars yet.</li>
                  ) : (
                    quoteConnections.pillars.map((pillar) => (
                      <li key={pillar.id} className="px-6 py-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center">
                              <Shield className="h-5 w-5 text-blue-600 mr-2" />
                              <h4 className="text-md font-semibold text-gray-800">{pillar.name}</h4>
                              <div className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${
                                pillar.impact_level === 'severe' ? 'bg-red-100 text-red-800' :
                                pillar.impact_level === 'moderate' ? 'bg-orange-100 text-orange-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {pillar.impact_level}
                              </div>
                              <button
                                onClick={() => {
                                  // Open modal to edit pillar justification
                                  setEditingPillarConnection(pillar);
                                  setEditJustificationText(pillar.justification || '');
                                  setEditImpactLevel(pillar.impact_level || 'moderate');
                                  setShowEditPillarModal(true);
                                }}
                                className="ml-2 text-blue-600 hover:text-blue-900"
                                title="Edit detailed threat analysis"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{pillar.justification}</p>
                          </div>
                          <button
                            onClick={() => {
                              if (confirm('Are you sure you want to remove this connection?')) {
                                setLoading(true);
                                setError(null);
                                
                                fetch(`/api/admin/quotes/${selectedQuote.id}/pillars/${pillar.id}`, {
                                  method: 'DELETE'
                                })
                                .then(response => {
                                  if (!response.ok) {
                                    throw new Error('Failed to delete pillar connection');
                                  }
                                  return response.json();
                                })
                                .then(() => {
                                  // Reload connections after successful deletion
                                  loadQuoteConnections(selectedQuote.id);
                                  alert('Pillar connection deleted successfully');
                                })
                                .catch(error => {
                                  console.error('Error deleting pillar connection:', error);
                                  setError(error.message);
                                })
                                .finally(() => {
                                  setLoading(false);
                                });
                              }
                            }}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </li>
                    ))
                  )}
                </ul>
              </div>
              
              {/* Connect to Authoritarian Tactics */}
              <div className="bg-white shadow overflow-hidden rounded-md">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">Authoritarian Tactics</h3>
                  <button 
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
                    onClick={() => setShowTacticModal(true)}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Connect to Tactic
                  </button>
                </div>
                <ul className="divide-y divide-gray-200">
                  {quoteConnections.tactics.length === 0 ? (
                    <li className="px-6 py-4 text-gray-500">No connected tactics yet.</li>
                  ) : (
                    quoteConnections.tactics.map((tactic) => (
                      <li key={tactic.id} className="px-6 py-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center">
                              <div className="bg-purple-100 text-purple-800 text-xs font-semibold px-2 py-1 rounded-full mr-2">
                                Step {tactic.playbook_step_number}
                              </div>
                              <h4 className="text-md font-semibold text-gray-800">{tactic.name}</h4>
                              <button
                                onClick={() => {
                                  // Open modal to edit tactic justification
                                  setEditingTacticConnection(tactic);
                                  setEditJustificationText(tactic.justification || '');
                                  setShowEditTacticModal(true);
                                }}
                                className="ml-2 text-purple-600 hover:text-purple-900"
                                title="Edit tactic justification"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{tactic.justification}</p>
                          </div>
                          <button
                            onClick={() => {
                              if (confirm('Are you sure you want to remove this connection?')) {
                                setLoading(true);
                                setError(null);
                                
                                fetch(`/api/admin/quotes/${selectedQuote.id}/tactics/${tactic.id}`, {
                                  method: 'DELETE'
                                })
                                .then(response => {
                                  if (!response.ok) {
                                    throw new Error('Failed to delete tactic connection');
                                  }
                                  return response.json();
                                })
                                .then(() => {
                                  // Reload connections after successful deletion
                                  loadQuoteConnections(selectedQuote.id);
                                  alert('Tactic connection deleted successfully');
                                })
                                .catch(error => {
                                  console.error('Error deleting tactic connection:', error);
                                  setError(error.message);
                                })
                                .finally(() => {
                                  setLoading(false);
                                });
                              }
                            }}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </li>
                    ))
                  )}
                </ul>
              </div>
              
              {/* Historical Precedents */}
              <div className="bg-white shadow overflow-hidden rounded-md">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">Historical Precedents</h3>
                  <button 
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700"
                    onClick={() => setShowPrecedentModal(true)}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Connect to Precedent
                  </button>
                </div>
                <ul className="divide-y divide-gray-200">
                  {quoteConnections.precedents.length === 0 ? (
                    <li className="px-6 py-4 text-gray-500">No connected precedents yet.</li>
                  ) : (
                    quoteConnections.precedents.map((precedent) => (
                      <li key={precedent.id} className="px-6 py-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center">
                              <h4 className="text-md font-semibold text-gray-800">
                                {precedent.country} <span className="text-gray-500 font-normal">({precedent.time_period})</span>
                              </h4>
                              <button
                                onClick={() => {
                                  // Open modal to edit precedent relevance
                                  setEditingPrecedentConnection(precedent);
                                  setEditJustificationText(precedent.relevance_note || '');
                                  setShowEditPrecedentModal(true);
                                }}
                                className="ml-2 text-amber-600 hover:text-amber-900"
                                title="Edit precedent relevance"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{precedent.relevance_note}</p>
                          </div>
                          <button
                            onClick={() => {
                              if (confirm('Are you sure you want to remove this connection?')) {
                                setLoading(true);
                                setError(null);
                                
                                fetch(`/api/admin/quotes/${selectedQuote.id}/precedents/${precedent.id}`, {
                                  method: 'DELETE'
                                })
                                .then(response => {
                                  if (!response.ok) {
                                    throw new Error('Failed to delete precedent connection');
                                  }
                                  return response.json();
                                })
                                .then(() => {
                                  // Reload connections after successful deletion
                                  loadQuoteConnections(selectedQuote.id);
                                  alert('Precedent connection deleted successfully');
                                })
                                .catch(error => {
                                  console.error('Error deleting precedent connection:', error);
                                  setError(error.message);
                                })
                                .finally(() => {
                                  setLoading(false);
                                });
                              }
                            }}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </li>
                    ))
                  )}
                </ul>
              </div>
            </div>
          )}
          
          {/* Modals */}
          <Modal 
            isOpen={showPillarModal} 
            onClose={() => setShowPillarModal(false)}
            title="Connect to Democratic Pillar"
          >
            <SelectPillarForm
              quote={selectedQuote}
              onSubmit={(data) => {
                handleConnectQuoteToPillar(selectedQuote.id, data.pillar_id, data.impact_level, data.justification);
                setShowPillarModal(false);
              }}
              onCancel={() => setShowPillarModal(false)}
            />
          </Modal>
          
          <Modal 
            isOpen={showTacticModal} 
            onClose={() => setShowTacticModal(false)}
            title="Connect to Authoritarian Tactic"
          >
            <SelectTacticForm
              quote={selectedQuote}
              onSubmit={(data) => {
                handleConnectQuoteToTactic(selectedQuote.id, data.tactic_id, data.justification);
                setShowTacticModal(false);
              }}
              onCancel={() => setShowTacticModal(false)}
            />
          </Modal>
          
          <Modal 
            isOpen={showPrecedentModal} 
            onClose={() => setShowPrecedentModal(false)}
            title="Connect to Historical Precedent"
          >
            <SelectPrecedentForm
              quote={selectedQuote}
              onSubmit={(data) => {
                handleConnectQuoteToPrecedent(selectedQuote.id, data.precedent_id, data.relevance_note);
                setShowPrecedentModal(false);
              }}
              onCancel={() => setShowPrecedentModal(false)}
            />
          </Modal>
        </div>
      )}
      
      {/* Democracy Indices Tab */}
      {activeSection === 'indices' && (
        <DemocracyIndicesTab />
      )}
      
      {/* Edit Pillar Justification Modal */}
      <Modal 
        isOpen={showEditPillarModal} 
        onClose={() => setShowEditPillarModal(false)}
        title="Edit Detailed Threat Analysis"
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="pillarJustification" className="block text-sm font-medium text-gray-700 mb-1">
              Detailed Analysis
            </label>
            <textarea
              id="pillarJustification"
              rows="8"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              value={editJustificationText}
              onChange={(e) => setEditJustificationText(e.target.value)}
              placeholder="Explain how this quote threatens democratic principles..."
            ></textarea>
          </div>
          
          <div>
            <label htmlFor="impactLevelSelect" className="block text-sm font-medium text-gray-700 mb-1">
              Impact Level
            </label>
            <select
              id="impactLevelSelect"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              value={editImpactLevel}
              onChange={(e) => setEditImpactLevel(e.target.value)}
            >
              <option value="severe">Severe</option>
              <option value="moderate">Moderate</option>
              <option value="mild">Mild</option>
            </select>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => setShowEditPillarModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                if (!editJustificationText.trim()) {
                  alert('Please enter a detailed analysis');
                  return;
                }
                
                handleEditPillarJustification(
                  selectedQuote.id, 
                  editingPillarConnection.id, 
                  editJustificationText,
                  editImpactLevel
                );
                setShowEditPillarModal(false);
              }}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
            >
              Save Changes
            </button>
          </div>
        </div>
      </Modal>
      
      {/* Edit Tactic Justification Modal */}
      <Modal 
        isOpen={showEditTacticModal} 
        onClose={() => setShowEditTacticModal(false)}
        title="Edit Authoritarian Tactic Justification"
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="tacticJustification" className="block text-sm font-medium text-gray-700 mb-1">
              Justification
            </label>
            <textarea
              id="tacticJustification"
              rows="8"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              value={editJustificationText}
              onChange={(e) => setEditJustificationText(e.target.value)}
              placeholder="Explain how this quote employs authoritarian tactics..."
            ></textarea>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => setShowEditTacticModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                if (!editJustificationText.trim()) {
                  alert('Please enter a justification');
                  return;
                }
                
                handleEditTacticJustification(
                  selectedQuote.id, 
                  editingTacticConnection.id, 
                  editJustificationText
                );
                setShowEditTacticModal(false);
              }}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none"
            >
              Save Changes
            </button>
          </div>
        </div>
      </Modal>
      
      {/* Edit Precedent Relevance Modal */}
      <Modal 
        isOpen={showEditPrecedentModal} 
        onClose={() => setShowEditPrecedentModal(false)}
        title="Edit Historical Precedent Relevance"
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="precedentRelevance" className="block text-sm font-medium text-gray-700 mb-1">
              Relevance Note
            </label>
            <textarea
              id="precedentRelevance"
              rows="8"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              value={editJustificationText}
              onChange={(e) => setEditJustificationText(e.target.value)}
              placeholder="Explain how this quote relates to the historical precedent..."
            ></textarea>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => setShowEditPrecedentModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                if (!editJustificationText.trim()) {
                  alert('Please enter a relevance note');
                  return;
                }
                
                handleEditPrecedentRelevance(
                  selectedQuote.id, 
                  editingPrecedentConnection.id, 
                  editJustificationText
                );
                setShowEditPrecedentModal(false);
              }}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 focus:outline-none"
            >
              Save Changes
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DemocraticFrameworkTab;